import { useState } from 'react';
import Modal from './Modal';

// Matches the server's rule (api/shared/identity.js): length only, capped at
// 10 (D24/D39). No character allowlist - output is escaped, and an allowlist
// would reject O'Brien and Jose. Not unique, so there's nothing to check.
const USERNAME_MAX = 10;

function UsernameSelectionModal({ isOpen, onClose, suggestedUsername, onSubmit, isSubmitting }) {
  const [username, setUsername] = useState(suggestedUsername || '');
  const [error, setError] = useState('');

  const validateUsername = (value) => {
    if (!value.trim()) {
      return 'Username is required';
    }
    if (value.trim().length > USERNAME_MAX) {
      return `Username must be ${USERNAME_MAX} characters or fewer`;
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    onSubmit(username.trim());
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const isValid = username.trim().length > 0 && username.trim().length <= USERNAME_MAX;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose Your Username">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-blue-800 text-sm mb-4">
            Welcome! Please choose a username to complete your account setup.
          </p>
          
          <label htmlFor="oauth-username" className="block text-blue-800 font-medium mb-2">
            Username
          </label>
          <input
            id="oauth-username"
            type="text"
            placeholder="Enter your username"
            maxLength={USERNAME_MAX}
            value={username}
            onChange={handleUsernameChange}
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 ${
              error
                ? 'bg-yellow-50 border-yellow-300 focus:ring-yellow-500'
                : 'border-blue-300 focus:ring-blue-800'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            autoFocus
          />
          
          <div className="mt-2 space-y-1">
            <p className="text-sm text-blue-600">
              • Up to {USERNAME_MAX} characters
            </p>
            <p className="text-sm text-blue-600">
              • This is just how you appear to friends — it doesn't have to be unique
            </p>
          </div>
          
          {error && (
            <p className="text-yellow-700 text-sm mt-2" role="alert">
              {error}
            </p>
          )}
        </div>
        
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 border-2 border-blue-300 text-blue-800 rounded-lg font-medium  transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
              isValid && !isSubmitting
                ? 'bg-yellow-400 border-2 border-blue-800 text-blue-800'
                : 'bg-blue-100 border-2 border-blue-300 text-blue-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Creating Account...' : 'Continue'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default UsernameSelectionModal;